/**
 * Cart drawer behaviour.
 *
 * There is no cart page and no cart icon in the header by design, so the only
 * entry point is Shopify checkout's own cart icon. That points at /cart, which
 * layout/theme.liquid bounces to the homepage with ?cart=open, handled below.
 *
 * After any change the section is re-rendered server side through the Section
 * Rendering API and swapped in, rather than the markup being rebuilt here.
 * That keeps sections/cart-drawer.liquid the single source of truth, so an
 * upsell added there needs no matching change in this file.
 */
(function () {
  var SECTION = 'cart-drawer';

  /* How long the empty cart stays on screen before the drawer closes itself.
     Long enough to read "Your cart is empty", short enough not to be in the
     way: emptying the cart should be visibly confirmed, not just make the
     panel vanish. */
  var EMPTY_LINGER_MS = 1200;
  var lingerTimer = null;

  function el() {
    return document.querySelector('[data-cart-drawer]');
  }

  /* The section stamps data-cart-empty on the dialog when the cart has nothing
     in it. */
  function isEmpty(drawer) {
    var target = drawer || el();
    return !!(target && target.hasAttribute('data-cart-empty'));
  }

  function cancelLinger() {
    if (!lingerTimer) return;
    clearTimeout(lingerTimer);
    lingerTimer = null;
  }

  /* Show the empty cart briefly, then close. Re-checks on firing, so an item
     arriving in the meantime (an upsell, say) cancels the close rather than
     shutting the drawer on someone mid-action. */
  function closeAfterEmptyLinger() {
    cancelLinger();
    lingerTimer = setTimeout(function () {
      lingerTimer = null;
      var drawer = el();
      if (drawer && drawer.open && isEmpty(drawer)) drawer.close();
    }, EMPTY_LINGER_MS);
  }

  function open() {
    var drawer = el();
    /* Arriving at an already empty cart is not the same as having just emptied
       one: there is nothing to confirm, so there is nothing worth showing. */
    if (!drawer || drawer.open || isEmpty(drawer)) return;
    drawer.showModal();
  }

  function close() {
    cancelLinger();
    var drawer = el();
    if (drawer && drawer.open) drawer.close();
  }

  function busy(state) {
    var drawer = el();
    if (drawer) drawer.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {})
    });
  }

  function refresh() {
    var current = el();
    var wasOpen = !!(current && current.open);
    busy(true);

    return fetch(window.location.pathname + '?sections=' + SECTION, {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        var holder = document.createElement('div');
        holder.innerHTML = data[SECTION];

        var next = holder.querySelector('[data-cart-drawer]');
        var existing = el();
        if (!next || !existing) throw new Error('Cart drawer markup missing');

        /* The old dialog is about to go, and with it any pending close. */
        cancelLinger();
        existing.replaceWith(next);

        /* replaceWith closes the old dialog, so reopen it. */
        if (!wasOpen) return;
        next.showModal();

        /* Emptying the cart shows the empty state for a beat, then closes. */
        if (isEmpty(next)) closeAfterEmptyLinger();
      })
      .catch(function () {
        /* A reload is still correct, just less graceful than an in place swap. */
        window.location.reload();
      });
  }

  /**
   * Change how many kits are in the cart.
   *
   * The kit is one subscription line plus its free extras, so the extras have
   * to move with it. Changing only the subscription line would send two kits
   * with one set of accessories.
   *
   * Each extra keeps its ratio to the subscription rather than being set to a
   * flat number, because that ratio differs per kit: the Single Kit carries one
   * of each, the Double Kit two. Two Double Kits therefore means four of each.
   *
   * Everything goes in a single /cart/update.js call, using the positional
   * array form so lines are matched by position rather than by variant.
   */
  function setKitQuantity(quantity) {
    busy(true);

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var subscriptionIndex = -1;
        var subscriptionQuantity = 0;

        cart.items.forEach(function (item, index) {
          if (item.selling_plan_allocation) {
            subscriptionIndex = index;
            subscriptionQuantity = item.quantity;
          }
        });

        /* No subscription line means there is no kit to scale against. */
        if (subscriptionIndex === -1 || subscriptionQuantity < 1) {
          throw new Error('No subscription line to scale against');
        }

        var updates = cart.items.map(function (item, index) {
          if (index === subscriptionIndex) return quantity;

          var perKit = item.quantity / subscriptionQuantity;
          return Math.max(1, Math.round(perKit * quantity));
        });

        return post('/cart/update.js', { updates: updates });
      })
      .then(refresh)
      .catch(function () {
        window.location.reload();
      });
  }

  document.addEventListener('click', function (event) {
    var drawer = el();

    if (event.target.closest('[data-cart-close]')) {
      close();
      return;
    }

    if (event.target.closest('[data-cart-clear]')) {
      post('/cart/clear.js').then(refresh);
      return;
    }

    var stepper = event.target.closest('[data-cart-qty]');
    if (stepper) {
      var quantity = parseInt(stepper.getAttribute('data-cart-qty'), 10);

      /* Taking the subscription to zero would leave the free extras behind in a
         cart with nothing to attach them to, so empty the cart instead. The
         refresh that follows shows the empty state, then closes. */
      if (!quantity || quantity < 1) {
        post('/cart/clear.js').then(refresh);
        return;
      }

      setKitQuantity(quantity);
      return;
    }

    /* A native dialog reports a backdrop click as a click on the dialog itself,
       so anything landing directly on the element is outside the panel. */
    if (drawer && drawer.open && event.target === drawer) close();
  });

  /* Escape closes the dialog natively, which would leave a pending close
     running against a drawer that has already gone. */
  document.addEventListener('close', function (event) {
    if (event.target && event.target.hasAttribute && event.target.hasAttribute('data-cart-drawer')) {
      cancelLinger();
    }
  }, true);

  /* Hooks for later work, e.g. an upsell that posts to /cart/add.js and then
     asks the drawer to redraw itself. */
  document.addEventListener('cart:refresh', refresh);
  document.addEventListener('cart:open', open);

  document.addEventListener('DOMContentLoaded', function () {
    var params = new URLSearchParams(window.location.search);
    if (params.get('cart') !== 'open') return;

    /* Tidy the flag away so a refresh does not reopen the drawer. */
    params.delete('cart');
    var query = params.toString();
    history.replaceState({}, '', window.location.pathname + (query ? '?' + query : ''));

    if (!el()) {
      /* The drawer is rendered by the layout so this should not happen, but if
         it ever does the customer still needs a way to edit their cart. */
      window.location.replace('/cart?nodrawer=1');
      return;
    }

    open();
  });
})();
